import dayjs from 'dayjs';
import QRCode from 'qrcode';

/**
 * 인증서 코드 생성기
 * 새로운 형식: MM + S8 (총 10자리)
 * MM: 제조사 코드 2자리 (대문자 A-Z)
 * S8: 시리얼 8자리 (대문자 A-Z, 숫자 0-9)
 */
export class CertificateCodeGenerator {
  /**
   * 인증서 고유 코드 생성
   * 형식: MM + S8 (총 10자리)
   * 예시: DM8K2T2450 (표시용), DM-8K2T2450 (입력용)
   */
  static generateCode(manufacturerCode = null) {
    // 제조사 코드가 제공되지 않으면 기본값 사용
    const mm = manufacturerCode ? this.normalizeManufacturerCode(manufacturerCode) : this.generateManufacturerCode();
    const s8 = this.generateSerialCode(8);
    
    return `${mm}${s8}`;
  }

  /**
   * 입력용 QR 코드 생성 (하이픈 포함)
   * 형식: MM-S8 (하이픈 포함으로 입력 편의성 제공)
   * 예시: DM-8K2T2450
   */
  static generateCodeForInput(manufacturerCode = null) {
    const mm = manufacturerCode ? this.normalizeManufacturerCode(manufacturerCode) : this.generateManufacturerCode();
    const s8 = this.generateSerialCode(8);
    
    return `${mm}-${s8}`;
  }

  /**
   * 제조사 코드 생성 (2자리 대문자)
   * @returns {string} 2자리 대문자 문자열
   */
  static generateManufacturerCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    
    for (let i = 0; i < 2; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    return result;
  }

  /**
   * 제조사 코드 정규화 (하이픈 제거 및 대문자 변환)
   * @param {string} manufacturerCode 제조사 코드
   * @returns {string} 정규화된 제조사 코드
   */
  static normalizeManufacturerCode(manufacturerCode) {
    if (!manufacturerCode || typeof manufacturerCode !== 'string') return '';
    return manufacturerCode.replace(/-/g, '').toUpperCase().trim().substring(0, 2);
  }

  /**
   * 시리얼 코드 생성 (영문 대문자 + 숫자)
   * 암호학적으로 안전한 난수 사용 (crypto.getRandomValues)
   * @param {number} length 생성할 길이
   * @returns {string} 시리얼 코드
   */
  static generateSerialCode(length) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';

    // 암호학적으로 안전한 난수 생성
    const randomValues = new Uint32Array(length);
    crypto.getRandomValues(randomValues);

    for (let i = 0; i < length; i++) {
      result += chars.charAt(randomValues[i] % chars.length);
    }

    return result;
  }

  /**
   * 랜덤 숫자 문자열 생성 (하위 호환성을 위해 유지)
   * 암호학적으로 안전한 난수 사용 (crypto.getRandomValues)
   * @param {number} length 생성할 길이
   */
  static generateRandomNumbers(length) {
    const chars = '0123456789';
    let result = '';

    // 암호학적으로 안전한 난수 생성
    const randomValues = new Uint32Array(length);
    crypto.getRandomValues(randomValues);

    for (let i = 0; i < length; i++) {
      result += chars.charAt(randomValues[i] % chars.length);
    }

    return result;
  }

  /**
   * QR코드 링크 URL 생성 (사용자가 접속할 URL)
   * @param {string} code 인증서 코드
   */
  static generateQRLinkUrl(code) {
    const baseUrl = this.getCustomerBaseUrl();
    const params = new URLSearchParams({
      qrCode: code,
      tenant: 'certilife',
      locale: 'ko'
    });
    return `${baseUrl}/mobile/qr-register?${params.toString()}`;
  }

  /**
   * 환경에 따른 customer 베이스 URL 동적 결정
   * 현재 도메인에서 프리픽스를 추출하여 해당 환경의 customer URL 반환
   * @returns {string} 환경별 customer 베이스 URL
   */
  static getCustomerBaseUrl() {
    // 서버 사이드 렌더링(SSR) 환경이거나 window 객체가 없는 경우 기본값 반환
    if (typeof window === 'undefined') {
      return 'https://customer.certi.life';
    }

    const currentDomain = window.location.hostname;

    // 현재 도메인에서 프리픽스 추출하여 환경 결정
    if (currentDomain.includes('localhost') || currentDomain.includes('127.0.0.1')) {
      // 로컬 환경은 intest와 동일한 주소 사용
      return 'https://intest-customer.certi.life';
    } else if (currentDomain.startsWith('stg-')) {
      // 스테이징 환경
      return 'https://stg-customer.certi.life';
    } else if (currentDomain.startsWith('intest-')) {
      // intest 환경
      return 'https://intest-customer.certi.life';
    } else {
      // 기본값: 운영 환경
      return 'https://customer.certi.life';
    }
  }

  /**
   * QR코드 이미지 생성 (qrcode 라이브러리 사용)
   * @param {string} code 인증서 코드
   * @param {Object} options QR코드 옵션
   */
  static async generateQRDataUrl(code, options = {}) {
    const linkUrl = this.generateQRLinkUrl(code);
    
    try {
      const qrOptions = {
        width: options.size || 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M',
        ...options
      };
      
      return await QRCode.toDataURL(linkUrl, qrOptions);
      
    } catch (error) {
      console.warn('qrcode 라이브러리 오류:', error);
      
      // 실패 시 Google Charts API 사용
      return this.generateQRImageUrlFallback(code, options.size || 200);
    }
  }

  /**
   * QR코드 이미지 생성 (Google Charts API - 대체 방식)
   * @param {string} code 인증서 코드
   * @param {number} size QR코드 크기 (기본값: 200)
   */
  static generateQRImageUrlFallback(code, size = 200) {
    const linkUrl = this.generateQRLinkUrl(code);
    const encodedUrl = encodeURIComponent(linkUrl);
    return `https://chart.googleapis.com/chart?chs=${size}x${size}&cht=qr&chl=${encodedUrl}`;
  }

  /**
   * QR코드 이미지 URL 생성 (호환성을 위한 메서드)
   * @param {string} code 인증서 코드
   * @param {number} size QR코드 크기 (기본값: 200)
   */
  static async generateQRImageUrl(code, size = 200) {
    try {
      // 먼저 qrcode 라이브러리로 시도
      return await this.generateQRDataUrl(code, { size });
    } catch (error) {
      // 실패 시 Google Charts API 사용
      return this.generateQRImageUrlFallback(code, size);
    }
  }

  /**
   * 코드 유효성 검증 (하이픈 포함/제외 모두 허용)
   * @param {string} code 검증할 코드
   * @returns {boolean} 유효성 여부
   */
  static validateCode(code) {
    if (!code || typeof code !== 'string') return false;
    
    // 하이픈 제거 후 정규화
    const normalizedCode = this.normalizeCode(code);
    
    // MM(2자리 대문자) + S8(8자리 대문자+숫자) = 총 10자리
    const pattern = /^[A-Z]{2}[A-Z0-9]{8}$/;
    return pattern.test(normalizedCode);
  }

  /**
   * 코드를 정규화 (하이픈 제거 및 대문자로 변환)
   * @param {string} code 입력 코드
   * @returns {string} 정규화된 코드 (하이픈 없는 10자리)
   */
  static normalizeCode(code) {
    if (!code || typeof code !== 'string') return '';
    return code.replace(/-/g, '').toUpperCase().trim();
  }

  /**
   * 표시용 코드 포맷 (하이픈 없음)
   * @param {string} code 입력 코드
   * @returns {string} 표시용 코드
   */
  static formatCodeForDisplay(code) {
    return this.normalizeCode(code);
  }

  /**
   * 입력용 코드 포맷 (하이픈 포함)
   * @param {string} code 입력 코드
   * @returns {string} 입력용 코드 (MM-S8 형식)
   */
  static formatCodeForInput(code) {
    const normalizedCode = this.normalizeCode(code);
    if (normalizedCode.length !== 10) return normalizedCode;
    
    const mm = normalizedCode.substring(0, 2);
    const s8 = normalizedCode.substring(2, 10);
    return `${mm}-${s8}`;
  }

  /**
   * 코드에서 제조사 코드 추출
   * @param {string} code 인증서 코드
   * @returns {string|null} 제조사 코드 (2자리) 또는 null
   */
  static extractManufacturerCode(code) {
    const normalizedCode = this.normalizeCode(code);
    if (!this.validateCode(normalizedCode)) return null;
    return normalizedCode.substring(0, 2);
  }

  /**
   * 코드에서 시리얼 부분 추출
   * @param {string} code 인증서 코드
   * @returns {string|null} 시리얼 코드 (8자리) 또는 null
   */
  static extractSerialCode(code) {
    const normalizedCode = this.normalizeCode(code);
    if (!this.validateCode(normalizedCode)) return null;
    return normalizedCode.substring(2, 10);
  }

  /**
   * 중복 체크용 쿼리 조건 생성
   * @param {string} code 생성된 코드
   */
  static getDuplicateCheckCondition(code) {
    const normalizedCode = this.normalizeCode(code);
    return {
      code: normalizedCode,
      // 전체 코드 기반으로 중복 체크 (년월 기반 제거)
    };
  }
}

export default CertificateCodeGenerator;
